import React, { useRef, useState } from "react";
import { WEAPON_ICONS } from "./legendData";

// ─── Types ────────────────────────────────────────────────────────────────────
type Mode = "single" | "triple";

const weaponNames = Object.keys(WEAPON_ICONS);

function getRandomWeapon(): string {
  return weaponNames[Math.floor(Math.random() * weaponNames.length)];
}

// ─── Component-scoped styles ────────────────────────────────────────────────────
function RandomizerStyles() {
  return (
    <style>{`
      .wr-mode-row { display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 20px; }
      .wr-mode-btn.inactive { background: #2a2a2a; color: var(--text-dim); }

      .wr-results {
        display: flex;
        flex-direction: column;
        gap: 14px;
        max-width: 420px;
      }
      .wr-result {
        display: flex;
        align-items: center;
        gap: 16px;
        background: var(--bg-card);
        border: 1px solid var(--border);
        border-radius: var(--radius-md);
        padding: 16px 20px;
      }
      .wr-result img {
        width: 48px;
        height: 48px;
        object-fit: contain;
        flex-shrink: 0;
      }
      .wr-result span {
        font-size: 1.3em;
        font-weight: 700;
      }
      .wr-empty {
        color: var(--text-muted);
        padding: 20px 0;
      }
    `}</style>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function WeaponRandomizerPage({ navigateTo }: { navigateTo: (path: string) => void }) {
  const [mode, setMode]       = useState<Mode>("single");
  const [results, setResults] = useState<string[]>([]);
  const lastWeaponRef = useRef<string | null>(null);

  const changeMode = (next: Mode) => {
    setMode(next);
    setResults([]);
    lastWeaponRef.current = null;
  };

  const randomize = () => {
    if (mode === "single") {
      let weapon: string;
      do {
        weapon = getRandomWeapon();
      } while (weapon === lastWeaponRef.current && weaponNames.length > 1);
      lastWeaponRef.current = weapon;
      setResults([weapon]);
    } else {
      // Triple mode: duplicates allowed, matching the original behavior
      setResults([getRandomWeapon(), getRandomWeapon(), getRandomWeapon()]);
    }
  };

  return (
    <div className="page-inner">
      <RandomizerStyles />

      <button onClick={() => navigateTo("/tools")} className="btn btn--secondary mb-16">
        ← Back to Tools
      </button>

      <div className="card roster-page-header">
        <div className="label-accent mb-12">QUIT</div>
        <div className="flex justify-between items-center gap-20 flex-wrap">
          <h1 style={{ margin: 0, fontSize: 46, lineHeight: 1.05 }}>Weapon Randomizer</h1>
        </div>
        <p className="text-muted mb-0 mt-8">Roll 1 or 3 random weapons.</p>
      </div>

      <div className="wr-mode-row">
        <button
          className={`btn${mode === "single" ? "" : " wr-mode-btn inactive"}`}
          onClick={() => changeMode("single")}
        >
          Single
        </button>
        <button
          className={`btn${mode === "triple" ? "" : " wr-mode-btn inactive"}`}
          onClick={() => changeMode("triple")}
        >
          Triple
        </button>
        <button className="btn btn--secondary" onClick={randomize}>
          Generate
        </button>
      </div>

      <div className="card">
        {results.length === 0 ? (
          <div className="wr-empty">Hit "Generate" to roll a weapon.</div>
        ) : (
          <div className="wr-results">
            {results.map((weapon, i) => (
              <div key={i} className="wr-result">
                <img src={WEAPON_ICONS[weapon]} alt={weapon} />
                <span>{weapon}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
